/**
 * AutoSyncMultimodalModal
 * ───────────────────────
 * The "Auto Sync" popup. Recordings synced from the Deskey device / mobile
 * app arrive untranscribed (`awaiting_transcribe`) and are listed here.
 * Picking one lets the teacher:
 *   • add blackboard / whiteboard photos (uploaded to its session)
 *   • tag a reference document (PDF upload or DeskeyHUB doc)
 *   • set a title
 * then "Transcribe" starts transcription + board OCR server-side. The session
 * card tracks progress; once extraction finishes the teacher opens the
 * review page and clicks "Generate Session".
 */
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Camera, Mic, X, ImageIcon, Sparkles, AlertTriangle, Trash2, Upload, BookOpen, Library, FileText, ArrowLeft, Inbox } from 'lucide-react';
import { InlineSparkle } from '../Loader';
import { toast } from 'sonner';
import TeacherHubDocPicker from './TeacherHubDocPicker';
import PendingAudioList, { pendingTitle, recordedAtLabel } from './PendingAudioList';
import { isBoardOcrBusy } from '../../lib/sessionStatus';
import { friendlyError, notifyError } from '../../lib/errors';
import { confirmDialog } from '../ConfirmDialog';

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_BOARDS = 20;
const MAX_BOARD_BYTES = 100 * 1024 * 1024;

const ATTACHED_OCR_POLL_MS = 3000;

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('desky_token') || ''}` });

// <img> can't send the Bearer header, so board-capture file URLs carry the
// token as `?auth=` (accepted by GET /teacher/board-captures/file/...).
const boardImageSrc = (url) => {
  const token = encodeURIComponent(localStorage.getItem('desky_token') || '');
  return `${API}${url}${url.includes('?') ? '&' : '?'}auth=${token}`;
};

const attachedLabel = (capture) => {
  if (isBoardOcrBusy(capture)) return 'Reading…';
  if (capture.ocr_status === 'failed') return 'Read failed';
  return 'On session';
};

async function postOrThrow(path, init) {
  const res = await fetch(`${API}/api/teacher${path}`, { method: 'POST', ...init, headers: { ...authHeaders(), ...(init.headers || {}) } });
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    throw new Error(d.detail || `Request failed (${res.status})`);
  }
  return res.json().catch(() => ({}));
}

export default function AutoSyncMultimodalModal({ open, onClose, onQueued, onDeleted, pendingItems = [], initialSessionId = null }) {
  const [selected, setSelected] = useState(null); // the pending recording being prepared
  const [attached, setAttached] = useState([]); // board captures already on its session (e.g. from mobile)
  const [boards, setBoards] = useState([]); // [{ file, url }]
  const [titleHint, setTitleHint] = useState('');
  const [referencePdf, setReferencePdf] = useState(null); // File
  const [referenceHubDoc, setReferenceHubDoc] = useState(null); // { id, title, file_ext }
  const [hubPickerOpen, setHubPickerOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const boardFileRef = useRef(null);
  const referenceFileRef = useRef(null);

  // Opening from a session card preselects that recording.
  useEffect(() => {
    if (!open) return;
    const pre = initialSessionId ? pendingItems.find(i => i.id === initialSessionId) : null;
    setSelected(pre || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialSessionId]);

  useEffect(() => {
    if (!selected) { setAttached([]); return; }
    setTitleHint(selected.title_hint || '');
  }, [selected]);

  // Photos already on the session (uploaded from mobile or an earlier visit).
  // Their OCR runs server-side right after upload, so keep refreshing while
  // any is still being read (pending -> processing -> done).
  const attachedBusy = attached.some(isBoardOcrBusy);
  useEffect(() => {
    if (!selected) return undefined;
    let cancelled = false;
    const load = () => fetch(`${API}/api/teacher/audio-inbox/${selected.id}/board-captures`, { headers: authHeaders() })
      .then(r => (r.ok ? r.json() : { items: [] }))
      .then(d => { if (!cancelled) setAttached(d.items || []); })
      .catch(() => {});
    load();
    const timer = attachedBusy ? setInterval(load, ATTACHED_OCR_POLL_MS) : null;
    return () => { cancelled = true; if (timer) clearInterval(timer); };
  }, [selected, attachedBusy]);

  // Revoke preview URLs on unmount.
  useEffect(() => () => boards.forEach(b => b.url && URL.revokeObjectURL(b.url)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);

  if (!open) return null;

  const clearForm = () => {
    boards.forEach(b => b.url && URL.revokeObjectURL(b.url));
    setBoards([]); setTitleHint(''); setReferencePdf(null); setReferenceHubDoc(null);
    setErrorMsg(null); setSelected(null);
  };

  const close = () => { if (!busy) { clearForm(); onClose(); } };

  // Delete a recording that's still waiting in Screening (audio + any board
  // photos). Backend: DELETE /api/teacher/audio-inbox/{id} (cascades captures).
  const deletePending = async (item) => {
    const ok = await confirmDialog({
      title: 'Delete recording',
      message: `Delete "${pendingTitle(item)}"? The recording and any board photos added to it will be removed. This can't be undone.`,
      danger: true,
      confirmLabel: 'Delete',
    });
    if (!ok) return;
    try {
      const res = await fetch(`${API}/api/teacher/audio-inbox/${item.id}`, { method: 'DELETE', headers: authHeaders() });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw Object.assign(new Error('Delete failed'), { status: res.status, body });
      }
      toast.success('Recording deleted');
      onDeleted && onDeleted(item.id);
    } catch (e) {
      notifyError(e, 'Could not delete the recording.');
    }
  };

  // ── Boards ───────────────────────────────────────────────────────────
  const onBoardFiles = (filesList) => {
    const next = [...boards];
    for (const f of Array.from(filesList || [])) {
      if (!/^image\/(jpeg|jpg|png|webp)$/i.test(f.type)) { toast.error(`Skipped ${f.name} — only JPEG/PNG/WEBP allowed.`); continue; }
      if (f.size > MAX_BOARD_BYTES) { toast.error(`Skipped ${f.name} — over 100 MB.`); continue; }
      next.push({ file: f, url: URL.createObjectURL(f) });
      if (next.length >= MAX_BOARDS) break;
    }
    setBoards(next);
  };

  const removeBoard = (i) => {
    setBoards(prev => {
      if (prev[i]?.url) URL.revokeObjectURL(prev[i].url);
      return prev.filter((_, idx) => idx !== i);
    });
  };

  // ── Transcribe: attach boards + reference to the session, then start it ─
  const transcribe = async () => {
    setBusy(true);
    setErrorMsg(null);
    try {
      let failed = 0;
      for (const b of boards) {
        const fd = new FormData();
        fd.append('file', b.file);
        fd.append('source_platform', 'web');
        try { await postOrThrow(`/audio-inbox/${selected.id}/board-captures`, { body: fd }); }
        catch { failed += 1; }
      }
      if (referencePdf || referenceHubDoc) {
        const fd = new FormData();
        if (referencePdf) fd.append('reference_pdf', referencePdf);
        else fd.append('reference_hub_doc_id', referenceHubDoc.id);
        await postOrThrow(`/auto-sync/${selected.id}/reference`, { body: fd });
      }
      await postOrThrow(`/auto-sync/${selected.id}/transcribe`, {
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title_hint: titleHint.trim() || null }),
      });
      if (failed) toast.error(`${failed} board photo(s) could not be uploaded.`);
      toast.success('Transcription started — open the session from Deskey Sessions to review & generate.');
      if (onQueued) onQueued(selected.id);
      setBusy(false);
      clearForm();
      onClose();
    } catch (e) {
      setErrorMsg(friendlyError(e, 'Failed.'));
      setBusy(false);
    }
  };

  // Portalled to document.body -- this component renders deep inside the
  // dashboard's scrollable content column (overflow-y-auto), which clips
  // ANY descendant to its own box regardless of position:fixed. Without
  // the portal, the backdrop only covers that column, visibly excluding
  // the sidebar and header strip above/beside it.
  return createPortal((
    <div className="fixed inset-0 !m-0 z-[140] flex items-end md:items-center justify-center" data-testid="auto-sync-modal" style={{ background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(6px)' }}>
      <div
        className="bg-white w-full md:w-[min(900px,94vw)] md:max-h-[88vh] rounded-t-2xl md:rounded-2xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-3">
          {selected ? (
            <button onClick={() => setSelected(null)} disabled={busy} className="w-9 h-9 rounded-xl hover:bg-slate-100 flex items-center justify-center disabled:opacity-50" data-testid="auto-sync-back">
              <ArrowLeft className="w-4 h-4" />
            </button>
          ) : (
            <div className="w-9 h-9 rounded-xl flex items-center justify-center text-white" style={{ background: '#1A7F37' }}>
              <Sparkles className="w-4 h-4" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold text-green-700">Screening</div>
            <div className="text-base font-semibold text-slate-900 truncate">{selected ? pendingTitle(selected) : 'Recorded sessions'}</div>
          </div>
          <button onClick={close} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center" data-testid="auto-sync-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="overflow-auto flex-1">
          <div className="p-5 grid gap-5">
            {!selected ? (
              pendingItems.length > 0 ? (
                <PendingAudioList items={pendingItems} onSelect={setSelected} onDelete={deletePending} disabled={busy} />
              ) : (
                <div className="rounded-2xl border-2 border-dashed border-slate-200 py-12 px-6 text-center" data-testid="auto-sync-pending-empty">
                  <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center mx-auto mb-3">
                    <Inbox className="w-5 h-5 text-slate-500" />
                  </div>
                  <p className="text-sm font-semibold text-slate-800">No recordings waiting</p>
                  <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">Recordings synced from your Deskey device show up here. Pick one to add board photos and a reference, then transcribe it.</p>
                </div>
              )
            ) : (
              <>
                {/* RECORDING */}
                <div className="rounded-2xl border border-green-200 bg-green-50/40 p-4 flex items-center gap-3" data-testid="auto-sync-selected-recording">
                  <div className="w-9 h-9 rounded-xl bg-green-100 text-green-700 flex items-center justify-center flex-shrink-0">
                    <Mic className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate">{selected.original_filename || pendingTitle(selected)}</div>
                    <div className="text-xs text-slate-500">Recorded {recordedAtLabel(selected)}</div>
                  </div>
                </div>

                {/* Title hint */}
                <input
                  type="text"
                  value={titleHint}
                  onChange={e => setTitleHint(e.target.value)}
                  placeholder="Title (optional)"
                  disabled={busy}
                  className="w-full h-11 px-4 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white text-sm focus:outline-none focus:border-green-400 transition"
                  data-testid="auto-sync-title-hint"
                />

                {/* BOARDS */}
                <div className="rounded-2xl border border-slate-200 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-semibold text-slate-600 flex items-center gap-1.5">
                      <ImageIcon className="w-3.5 h-3.5" /> Board photos · {boards.length}/{MAX_BOARDS}
                      {attached.length > 0 && (
                        <span className="normal-case tracking-normal font-semibold text-green-700">· {attached.length} already attached</span>
                      )}
                    </div>
                    <input
                      ref={boardFileRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      multiple
                      capture="environment"
                      className="hidden"
                      onChange={e => { onBoardFiles(e.target.files); e.target.value = ''; }}
                      data-testid="auto-sync-board-files"
                    />
                    <button
                      type="button"
                      onClick={() => boardFileRef.current?.click()}
                      disabled={busy || boards.length >= MAX_BOARDS}
                      data-testid="auto-sync-add-board"
                      className="inline-flex items-center gap-1.5 px-3 h-9 rounded-xl bg-green-500 hover:bg-green-600 text-white text-xs font-semibold disabled:opacity-50">
                      <Camera className="w-3.5 h-3.5" /> Add boards
                    </button>
                  </div>
                  {boards.length === 0 && attached.length === 0 ? (
                    <div
                      className="rounded-xl border-2 border-dashed border-slate-200 py-6 text-center text-xs text-slate-500 cursor-pointer hover:bg-slate-50"
                      onClick={() => boardFileRef.current?.click()}
                      data-testid="auto-sync-board-empty">
                      Click to pick blackboard / whiteboard photos. JPEG / PNG / WEBP, ≤100 MB each.
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                      {attached.map((c, i) => {
                        const url = c.processed_image_url || c.raw_image_url;
                        return (
                          <div key={c.capture_id} className="relative rounded-lg overflow-hidden border border-green-200 aspect-square bg-slate-50" data-testid={`auto-sync-attached-board-${i}`}>
                            {url && <img src={boardImageSrc(url)} alt={`attached-board-${i}`} className="w-full h-full object-cover" />}
                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent text-white text-xs font-semibold p-1">
                              {attachedLabel(c)}
                            </div>
                          </div>
                        );
                      })}
                      {boards.map((b, i) => (
                        <div key={b.url} className="relative group rounded-lg overflow-hidden border border-slate-200 aspect-square" data-testid={`auto-sync-board-${i}`}>
                          <img src={b.url} alt={`board-${i}`} className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={() => removeBoard(i)}
                            disabled={busy}
                            className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                            data-testid={`auto-sync-board-remove-${i}`}>
                            <Trash2 className="w-3 h-3" />
                          </button>
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent text-white text-xs font-semibold p-1">
                            Board {i + 1}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* REFERENCE DOCUMENT */}
                <div className="rounded-2xl border border-slate-200 p-4">
                  <div className="text-xs font-semibold text-slate-600 flex items-center gap-1.5 mb-2">
                    <BookOpen className="w-3.5 h-3.5" /> Reference document (optional)
                  </div>
                  {!referencePdf && !referenceHubDoc ? (
                    <div className="flex flex-wrap gap-2">
                      <input
                        ref={referenceFileRef}
                        type="file"
                        accept=".pdf,application/pdf"
                        className="hidden"
                        onChange={e => {
                          const f = e.target.files?.[0];
                          e.target.value = '';
                          if (!f) return;
                          setReferenceHubDoc(null);
                          setReferencePdf(f);
                        }}
                        data-testid="auto-sync-reference-file"
                      />
                      <button
                        type="button"
                        onClick={() => referenceFileRef.current?.click()}
                        disabled={busy}
                        data-testid="auto-sync-upload-reference"
                        className="inline-flex items-center gap-1.5 px-3 h-9 rounded-xl bg-white border border-slate-200 hover:bg-slate-50 text-xs font-semibold disabled:opacity-50">
                        <Upload className="w-3.5 h-3.5" /> Upload PDF
                      </button>
                      <button
                        type="button"
                        onClick={() => setHubPickerOpen(true)}
                        disabled={busy}
                        data-testid="auto-sync-tag-hub-doc"
                        className="inline-flex items-center gap-1.5 px-3 h-9 rounded-xl bg-white border border-slate-200 hover:bg-slate-50 text-xs font-semibold disabled:opacity-50">
                        <Library className="w-3.5 h-3.5" /> Tag from DeskeyHUB
                      </button>
                    </div>
                  ) : (
                    <div className="inline-flex items-center gap-2 pl-3 pr-1.5 h-9 rounded-xl bg-green-50 border border-green-200 text-xs font-semibold text-green-800" data-testid="auto-sync-reference-chip">
                      <FileText className="w-3.5 h-3.5" />
                      <span className="max-w-[220px] truncate">{referencePdf ? referencePdf.name : referenceHubDoc.title}</span>
                      <button
                        type="button"
                        onClick={() => { setReferencePdf(null); setReferenceHubDoc(null); }}
                        disabled={busy}
                        data-testid="auto-sync-reference-remove"
                        className="w-6 h-6 rounded-full hover:bg-green-100 flex items-center justify-center disabled:opacity-50">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                  <p className="text-xs text-slate-400 mt-2">Tag a syllabus or textbook PDF and Deskey will highlight the parts of the lecture drawn straight from it.</p>
                </div>

                {busy && (
                  <div className="rounded-2xl border border-green-200 bg-green-50 p-4 flex items-center gap-3" data-testid="auto-sync-progress">
                    <InlineSparkle size={22} label="Starting transcription" />
                    <div className="text-sm font-semibold text-green-900">Attaching boards & starting transcription…</div>
                  </div>
                )}
              </>
            )}

            {errorMsg && (
              <div className="rounded-2xl border border-red-200 bg-red-50 p-4 flex items-center gap-3" data-testid="auto-sync-error">
                <AlertTriangle className="w-4 h-4 text-red-600" />
                <div className="text-sm font-semibold text-red-900">{errorMsg}</div>
              </div>
            )}
          </div>
        </div>

        {/* Footer — only in the recording detail view; the list view exits via the header X */}
        {selected && (
          <div className="px-5 py-3 border-t border-slate-100 flex flex-wrap gap-2 justify-end">
            <button onClick={close} disabled={busy} className="h-10 px-4 rounded-xl bg-white border border-slate-200 text-xs font-semibold hover:bg-slate-50 disabled:opacity-50" data-testid="auto-sync-cancel">
              Cancel
            </button>
            <button
              onClick={transcribe}
              disabled={busy}
              data-testid="auto-sync-transcribe"
              className="h-10 px-5 rounded-xl text-white text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-50 transition hover:shadow-md"
              style={{ background: '#1A7F37' }}>
              {busy ? <InlineSparkle size={16} label="Starting transcription" /> : <Sparkles className="w-3.5 h-3.5" />}
              {busy ? 'Starting…' : 'Transcribe'}
            </button>
          </div>
        )}
      </div>
      {hubPickerOpen && (
        <TeacherHubDocPicker
          onClose={() => setHubPickerOpen(false)}
          onSelect={(doc) => { setReferenceHubDoc(doc); setReferencePdf(null); setHubPickerOpen(false); }}
        />
      )}
    </div>
  ), document.body);
}
